import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A lightweight markdown parser component that converts markdown text into styled React elements.
 * Handles headings (H2, H3), paragraphs, lists (unordered), bold text, links, blockquotes.
 */
export default function MarkdownRenderer({ content }) {
  if (!content) return null;

  // Strip frontmatter if present
  let cleanContent = content;
  if (cleanContent.startsWith('---')) {
    const endFrontmatterIndex = cleanContent.indexOf('---', 3);
    if (endFrontmatterIndex !== -1) {
      cleanContent = cleanContent.substring(endFrontmatterIndex + 3).trim();
    }
  }

  // Split into paragraphs / blocks
  const blocks = cleanContent.split(/\n\s*\n/);

  return (
    <div className="markdown-content">
      {blocks.map((block, bIdx) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // Heading 2
        if (trimmed.startsWith('## ')) {
          const headingText = trimmed.replace(/^##\s+/, '');
          return <h2 key={bIdx}>{parseInlineMarkdown(headingText)}</h2>;
        }

        // Heading 3
        if (trimmed.startsWith('### ')) {
          const headingText = trimmed.replace(/^###\s+/, '');
          return <h3 key={bIdx}>{parseInlineMarkdown(headingText)}</h3>;
        }

        // Heading 1
        if (trimmed.startsWith('# ')) {
          const headingText = trimmed.replace(/^#\s+/, '');
          return <h1 key={bIdx}>{parseInlineMarkdown(headingText)}</h1>;
        }

        // Bullet list
        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
          const items = trimmed.split(/\n[*|-]\s+/).map(item => item.replace(/^[*|-]\s+/, '').trim()).filter(Boolean);
          return (
            <ul key={bIdx} className="blog-bullet-list">
              {items.map((item, iIdx) => (
                <li key={iIdx}>{parseInlineMarkdown(item)}</li>
              ))}
            </ul>
          );
        }

        // Blockquote / Callout
        if (trimmed.startsWith('> ')) {
          const quoteText = trimmed.replace(/^>\s+/, '');
          return (
            <blockquote key={bIdx} className="blog-callout">
              <p>{parseInlineMarkdown(quoteText)}</p>
            </blockquote>
          );
        }

        // Standard Paragraph
        return <p key={bIdx}>{parseInlineMarkdown(trimmed)}</p>;
      })}
    </div>
  );
}

/**
 * Helper function to parse inline markdown bold (**bold**), links ([text](/url)), and code (`code`)
 */
function parseInlineMarkdown(text) {
  if (!text) return '';

  // Regex pattern matching links [text](url) and bold **text**
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text prior to match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matchedStr = match[0];
    if (matchedStr.startsWith('**') && matchedStr.endsWith('**')) {
      // Bold text
      const boldText = matchedStr.slice(2, -2);
      parts.push(<strong key={match.index}>{boldText}</strong>);
    } else if (matchedStr.startsWith('[')) {
      // Link
      const linkMatch = /\[([^\]]+)\]\(([^)]+)\)/.exec(matchedStr);
      if (linkMatch) {
        const linkText = linkMatch[1];
        const linkUrl = linkMatch[2];

        if (linkUrl.startsWith('http')) {
          parts.push(
            <a key={match.index} href={linkUrl} target="_blank" rel="noopener noreferrer" className="blog-link">
              {linkText}
            </a>
          );
        } else {
          parts.push(
            <Link key={match.index} to={linkUrl} className="blog-link">
              {linkText}
            </Link>
          );
        }
      }
    }

    lastIndex = regex.lastIndex;
  }

  // Append remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}
